import { PopUpMessageContext, useApp, useAuth } from '@hb/react'
import {
  adminPracticeContractsCollection,
  BooleanField,
  defaultInitialValue,
  FieldMap,
  FieldMapValue,
  FieldTypes,
  FileField,
  OnUploadProgress,
  TextField,
} from '@hb/shared'
import { FORM_ERROR } from 'final-form'
import { useCallback, useContext } from 'react'
import { addItem } from '../../../../backend/db/utils/collections'
import { processFieldMapData } from '../../../../backend/db/utils/processData'
import { SimpleForm } from '../../../forms'
import { OnFormSubmit } from '../../../forms/FinalForm/types'
import { addExternalPdfTemplate } from '../../utils'

const isExternalPdfField: BooleanField = {
  type: FieldTypes.BOOLEAN,
  yesText: 'Yes',
  noText: 'No',
  placeholder: 'Create from PDF?',
}

const externalPdfField: FileField = {
  type: FieldTypes.FILE,
  placeholder: 'Practice Contract PDF',
  fileType: 'pdf',
  name: 'externalPdf',
  condition: v => !!v?.isExternalPdf,
}

const nameField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Practice Contract Name',
}

const templateField: FieldMap = {
  name: 'New Practice Contract Template',
  children: {
    name: nameField,
    isExternalPdf: isExternalPdfField,
    externalPdf: externalPdfField,
  },
}

export const AddPracticeContract = ({ onClose }: { onClose: () => void }) => {
  const { appName } = useApp()
  const { authUser, admin } = useAuth()
  const { processResponse } = useContext(PopUpMessageContext)

  const onCreate: OnFormSubmit<FieldMapValue> = useCallback(
    async (values: FieldMapValue, onUploadProgress: OnUploadProgress) => {
      const uid = authUser?.uid
      if (!uid) {
        return { [FORM_ERROR]: 'Not signed in' }
      }
      try {
        const now = new Date().toISOString()
        if (values.isExternalPdf) {
          if (!values.externalPdf) {
            return { [FORM_ERROR]: 'Uploaded contract selected but no PDF found' }
          }
          const processed = await processFieldMapData(
            appName,
            admin,
            uid,
            `temp/templates/${now}`,
            templateField,
            values,
            null,
            onUploadProgress,
          )
          await addExternalPdfTemplate({
            templateType: 'practiceContract',
            name: processed.name,
            isExternalPdf: processed.isExternalPdf,
            externalPdf: processed.externalPdf,
            practiceId: null,
          })
        } else {
          await addItem(appName, adminPracticeContractsCollection, {
            ...values,
            editorVersion: 'v2',
            templateText: defaultInitialValue,
          })
        }
      } catch (err: any) {
        return processResponse({ error: err?.message || 'An error occurred' })
      }
      const res = processResponse({ success: 'Practice contract created' })
      onClose()
      return res
    },
    [appName, authUser, admin, processResponse, onClose],
  )

  return <SimpleForm field={templateField} onSubmit={onCreate} theme="detailed" />
}
